import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useLanguage } from '../context/LanguageContext';

function ResetPassword() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [tokenStatus, setTokenStatus] = useState('validating'); // validating | valid | invalid
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  // Validate the token when the page loads
  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid');
      return;
    }

    api.get(`/auth/validate-reset-token?token=${token}`)
      .then(({ data }) => {
        setTokenStatus(data.valid ? 'valid' : 'invalid');
      })
      .catch(() => {
        setTokenStatus('invalid');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (password.length < 6) {
      setErrorMsg(t('password_too_short'));
      return;
    }

    if (password !== confirmPassword) {
      setErrorMsg(t('passwords_dont_match'));
      return;
    }

    setSubmitStatus('loading');

    try {
      await api.post('/auth/reset-password', { token, password });
      setSubmitStatus('success');
      // Redirect to login after 3 seconds
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setSubmitStatus('error');
      setErrorMsg(
        err.response?.data?.error || t('error_generic')
      );
    }
  };

  // ── Validating token ──────────────────────────────────────────────────────
  if (tokenStatus === 'validating') {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
        background: 'var(--bg-apex)'
      }}>
        <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
          <p style={{ color: 'var(--text-body)', fontWeight: 500 }}>{t('reset_token_validating')}</p>
        </div>
      </div>
    );
  }

  // ── Invalid / expired token ───────────────────────────────────────────────
  if (tokenStatus === 'invalid') {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
        background: 'var(--bg-apex)',
        backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.05), transparent 70%)'
      }}>
        <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem', textAlign: 'center' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'rgba(244, 114, 182, 0.12)', border: '2px solid rgba(244, 114, 182, 0.3)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '1.5rem', fontSize: '1.75rem'
          }}>
            ✕
          </div>
          <h2 style={{ color: 'var(--text-heading)', fontSize: '1.5rem', marginBottom: '1rem' }}>
            {t('reset_token_invalid_title')}
          </h2>
          <p style={{ color: 'var(--text-body)', fontSize: '0.9rem', marginBottom: '2rem', lineHeight: 1.6 }}>
            {t('reset_token_invalid_subtitle')}
          </p>
          <Link to="/forgot-password" className="btn-cyber" style={{ display: 'inline-block', padding: '0.85rem 2rem', textDecoration: 'none' }}>
            {t('new_reset_link')}
          </Link>
          <div style={{ marginTop: '1.25rem' }}>
            <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>
              ← {t('back_to_login')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Success state ─────────────────────────────────────────────────────────
  if (submitStatus === 'success') {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
        background: 'var(--bg-apex)',
        backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.05), transparent 70%)'
      }}>
        <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem', textAlign: 'center' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'rgba(34, 197, 94, 0.15)', border: '2px solid rgba(34, 197, 94, 0.4)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '1.5rem', fontSize: '1.75rem'
          }}>
            ✓
          </div>
          <h2 style={{ color: 'var(--text-heading)', fontSize: '1.5rem', marginBottom: '1rem' }}>
            {t('reset_success_title')}
          </h2>
          <div style={{
            background: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.2)',
            color: '#4ade80', padding: '1rem 1.25rem', borderRadius: '12px',
            marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: 1.6
          }}>
            {t('reset_success_subtitle')}
          </div>
          <Link to="/login" style={{ color: 'var(--accent-primary)', fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none' }}>
            {t('go_to_login_now')}
          </Link>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh',
      background: 'var(--bg-apex)',
      backgroundImage: 'radial-gradient(circle at center, rgba(99, 102, 241, 0.05), transparent 70%)'
    }}>
      <div className="glass-card animate-fade" style={{ width: '100%', maxWidth: '440px', padding: '3rem' }}>

        {/* Logo & Title */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <div style={{
            background: 'var(--accent-primary)',
            width: '80px', height: '80px', borderRadius: '20px', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', marginBottom: '2rem',
            boxShadow: 'var(--glow-indigo)'
          }}>
            <span style={{ fontWeight: 900, color: 'var(--btn-text)', fontSize: '2.5rem' }}>A</span>
          </div>
          <h2 style={{ fontSize: '2rem', color: 'var(--text-heading)', marginBottom: '0.75rem', letterSpacing: '-0.03em' }}>
            {t('new_password')}
          </h2>
          <p style={{ color: 'var(--text-body)', fontSize: '0.95rem', fontWeight: 500 }}>
            {t('forgot_password_subtitle')}
          </p>
        </div>

        {/* Error */}
        {(submitStatus === 'error' || errorMsg) && (
          <div style={{
            background: 'rgba(244, 114, 182, 0.1)', border: '1px solid rgba(244, 114, 182, 0.2)',
            color: 'var(--accent-magenta)', padding: '1rem', borderRadius: '12px',
            marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600
          }}>
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* New password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{
              fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem'
            }}>
              {t('new_password_label')}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                className="cyber-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitStatus === 'loading'}
                style={{ paddingRight: '3rem', width: '100%', boxSizing: 'border-box' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--text-muted)', fontSize: '1rem', padding: '0.25rem'
                }}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {/* Confirm password */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <label style={{
              fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.05em', marginLeft: '0.5rem'
            }}>
              {t('confirm_password_label')}
            </label>
            <input
              className="cyber-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={submitStatus === 'loading'}
            />
          </div>

          {/* Password strength hint */}
          {password.length > 0 && (
            <div style={{
              fontSize: '0.78rem', color: password.length >= 6 ? '#4ade80' : '#f87171',
              marginTop: '-0.5rem', marginLeft: '0.5rem', fontWeight: 600
            }}>
              {password.length >= 6 ? t('password_hint_valid') : t('password_hint_invalid').replace('{count}', (6 - password.length).toString())}
            </div>
          )}

          <button
            type="submit"
            className="btn-cyber"
            disabled={submitStatus === 'loading'}
            style={{ marginTop: '0.5rem', padding: '1.25rem', opacity: submitStatus === 'loading' ? 0.7 : 1 }}
          >
            {submitStatus === 'loading' ? t('saving') : t('change_password')}
          </button>
        </form>

        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Link to="/login" style={{
            color: 'var(--accent-primary)', fontWeight: 700,
            fontSize: '0.85rem', textDecoration: 'none'
          }}>
            ← {t('back_to_login')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
